import type { AudioToTextResponse, ConversationsResponse, MessageDetailRequest, RenameConversationRequest, Request, SendDTO, StopMessageRequest, TextToSpeechRequest, UploadFileResponse } from './types';
import { resolveHttpErrorMessage } from '@/utils/httpErrorMessage';
import { del, get, post, put } from '@/utils/request';

const DEFAULT_FALLBACK_TOKEN = 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJsb2dpblR5cGUiOiJsb2dpbiIsImxvZ2luSWQiOiJzeXNfdXNlcjoxIiwicm5TdHIiOiJKSDBFSDhJTTR6Y2ZXODhhN1g0UUtZVzg3b01OQlBWYSIsInRlbmFudElkIjoiMDAwMDAiLCJ1c2VySWQiOjF9.mmhTQtmV8BO6-UDZl402DBb3Zg79DJKENeMwsdsKh2M';
const CHAT_PREFIX = '/chat/upload';

function resolveChatEndpoint(path: string) {
  if (!path.startsWith('/'))
    path = `/${path}`;
  const remoteBase = import.meta.env.VITE_API_URL || import.meta.env.VITE_WEB_BASE_API || '';
  if (import.meta.env.DEV) {
    if (remoteBase && path.startsWith('/chat')) {
      return `${remoteBase}`.replace(/\/$/, '') + path;
    }
    return path;
  }
  if (remoteBase)
    return `${remoteBase}`.replace(/\/$/, '') + path;
  return path;
}

function pickFirstNonEmptyString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (value === undefined || value === null)
      continue;
    const text = String(value).trim();
    if (text)
      return text;
  }
  return undefined;
}

function pickFirstFiniteNumber(...values: unknown[]): number | undefined {
  for (const value of values) {
    const num = Number(value);
    if (Number.isFinite(num) && num >= 0)
      return num;
  }
  return undefined;
}

function normalizeUploadFileResponse(payload: any) {
  if (!payload || typeof payload !== 'object')
    return payload;

  const nestedData = payload.data && typeof payload.data === 'object' ? payload.data : undefined;
  const nestedFile = payload.file && typeof payload.file === 'object' ? payload.file : undefined;
  const nestedResult = payload.result && typeof payload.result === 'object' ? payload.result : undefined;

  const id = pickFirstNonEmptyString(
    payload.id,
    payload.upload_file_id,
    payload.file_id,
    payload.fileId,
    payload.uploadFileId,
    payload.uploadId,
    nestedData?.id,
    nestedData?.upload_file_id,
    nestedData?.file_id,
    nestedData?.fileId,
    nestedFile?.id,
    nestedFile?.upload_file_id,
    nestedFile?.file_id,
    nestedResult?.id,
    nestedResult?.upload_file_id,
    nestedResult?.file_id,
  );

  const url = pickFirstNonEmptyString(
    payload.url,
    payload.file_url,
    payload.fileUrl,
    payload.download_url,
    payload.downloadUrl,
    nestedData?.url,
    nestedData?.file_url,
    nestedData?.fileUrl,
    nestedFile?.url,
    nestedFile?.file_url,
    nestedResult?.url,
  );

  const filename = pickFirstNonEmptyString(
    payload.filename,
    payload.name,
    payload.original_filename,
    payload.originalFilename,
    nestedData?.filename,
    nestedData?.name,
    nestedData?.original_filename,
    nestedFile?.filename,
    nestedFile?.name,
    nestedResult?.filename,
    nestedResult?.name,
  );

  const bytes = pickFirstFiniteNumber(
    payload.bytes,
    payload.size,
    payload.file_size,
    payload.fileSize,
    nestedData?.bytes,
    nestedData?.size,
    nestedData?.file_size,
    nestedFile?.bytes,
    nestedFile?.size,
    nestedResult?.bytes,
    nestedResult?.size,
  );

  const createdAt = pickFirstFiniteNumber(
    payload.created_at,
    payload.createdAt,
    nestedData?.created_at,
    nestedFile?.created_at,
    nestedResult?.created_at,
  );

  return {
    ...payload,
    id: id ?? payload.id,
    url: url ?? payload.url,
    filename: filename ?? payload.filename,
    bytes: bytes ?? payload.bytes,
    created_at: createdAt ?? payload.created_at,
  };
}

// 发送消息（基于 Nodes 的新接口）
// 与 /chat/send 请求参数保持一致，但走新的后端实现
export function sendMessageWithNodes(data: Request) {
  return post<null>('/chat/sendWithNodes', data);
}

// 发送消息（原 /chat/send 接口，保留作为备用）
export function sendMessage(data: Request) {
  return post<null>('/chat/send', data);
}

// 发送消息（旧 DTO 形式，保留以兼容历史代码）
export function send(data: SendDTO) {
  return post<null>('/chat/send', data);
}

// 获取历史对话列表
export function getConversations() {
  return get<ConversationsResponse>('/system/session/list').json();
}

// 获取历史对话详情
export function getMessageDetail(params: MessageDetailRequest) {
  console.log('[API] /system/message/dify/messages 请求参数:', JSON.stringify(params, null, 2));
  return get('/system/message/dify/messages', params).json();
}

// 删除历史对话
export function deleteConversations(ids: string | number | Array<string | number>) {
  let formatted = '';
  if (Array.isArray(ids)) {
    formatted = ids.join(',');
  }
  else {
    formatted = String(ids);
  }
  const pathParam = encodeURIComponent(formatted);
  return del(`/system/session/${pathParam}`).json();
}

// 重命名历史对话
export function renameConversation(params: RenameConversationRequest) {
  return put('/system/session', params).json();
}

// 停止消息响应（使用 Query 参数）
export function stopMessage(data: StopMessageRequest) {
  const queryParams = new URLSearchParams();
  queryParams.append('agentId', String(data.agentId));
  if (data.taskId) {
    queryParams.append('taskId', data.taskId);
  }
  if (data.chatId) {
    queryParams.append('chatId', data.chatId);
  }
  if (data.conversationId) {
    queryParams.append('conversationId', data.conversationId);
  }
  return post<any>(`/chat/stop?${queryParams.toString()}`).json();
}

// 文本转语音
export async function textToSpeech(data: TextToSpeechRequest): Promise<Blob> {
  const { useUserStore } = await import('@/stores');
  const userStore = useUserStore();
  const fallbackToken = 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJsb2dpblR5cGUiOiJsb2dpbiIsImxvZ2luSWQiOiJzeXNfdXNlcjoxIiwicm5TdHIiOiJKSDBFSDhJTTR6Y2ZXODhhN1g0UUtZVzg3b01OQlBWYSIsInRlbmFudElkIjoiMDAwMDAiLCJ1c2VySWQiOjF9.mmhTQtmV8BO6-UDZl402DBb3Zg79DJKENeMwsdsKh2M';
  const token = userStore.token || fallbackToken;

  // URL 构建逻辑（与 uploadChatFile 保持一致）
  const remoteBase = import.meta.env.VITE_API_URL || import.meta.env.VITE_WEB_BASE_API || '';
  let url = '/chat/speech';

  // 开发环境：如果配置了远程地址，则使用远程地址
  if (import.meta.env.DEV && remoteBase) {
    url = `${remoteBase}`.replace(/\/$/, '') + `/${url}`.replace(/^\//, '');
  }
  // 生产环境：使用配置的 baseURL
  else if (!import.meta.env.DEV && remoteBase) {
    url = `${remoteBase}`.replace(/\/$/, '') + `/${url}`.replace(/^\//, '');
  }

  console.log('[TTS API] url:', url);
  console.log('[TTS API] payload:', data);

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const contentType = response.headers.get('content-type') || '';
  console.log('[TTS API] status:', response.status, response.statusText);
  console.log('[TTS API] content-type:', contentType);
  if (!response.ok || contentType.includes('application/json')) {
    const cloned = response.clone();
    let errorMessage = '文本转语音失败';
    try {
      const errorJson = await cloned.json();
      errorMessage = errorJson.message || errorJson.msg || errorMessage;
    }
    catch {
      const errorText = await response.text();
      if (errorText)
        errorMessage = errorText;
    }
    throw new Error(errorMessage);
  }

  return await response.blob();
}

// 上传聊天附件
export async function uploadChatFile(agentId: string | number, file: File) {
  if (!agentId)
    throw new Error('缺少智能体ID');
  if (!file)
    throw new Error('请选择文件');

  // 开发环境回归测试：.env.development 中设置 VITE_MOCK_UPLOAD_FAIL=true 后，上传必定失败
  if (import.meta.env.DEV && String(import.meta.env.VITE_MOCK_UPLOAD_FAIL) === 'true') {
    await new Promise(resolve => setTimeout(resolve, 400));
    throw new Error('模拟上传失败（测试）');
  }

  // 部分后端校验依赖 mime/type 或扩展名大小写，这里对常见 CSV 做最小规范化兜底。
  const normalized = (() => {
    const originalName = file.name;
    const extMatch = originalName.match(/\.([^.]+)$/);
    const ext = (extMatch?.[1] || '').toLowerCase();

    if (ext !== 'csv')
      return { file, name: originalName };

    const nextName = originalName.replace(/\.([^.]+)$/i, '.csv');
    // 如果浏览器没有给出合适的 mime，强制给 text/csv 以提升兼容性。
    const nextType = file.type && file.type.toLowerCase().includes('csv') ? file.type : 'text/csv';
    if (nextName === originalName && nextType === file.type)
      return { file, name: originalName };

    // 用新的 File 包一层，覆盖 type，并尽量保持内容不变。
    const nextFile = new File([file], nextName, { type: nextType });
    return { file: nextFile, name: nextName };
  })();

  const formData = new FormData();
  formData.append('file', normalized.file, normalized.name);

  const endpoint = resolveChatEndpoint(CHAT_PREFIX);
  const query = new URLSearchParams();
  query.append('agentId', String(agentId));
  const url = `${endpoint}?${query.toString()}`;

  const { useUserStore } = await import('@/stores');
  const userStore = useUserStore();
  const token = userStore.token || DEFAULT_FALLBACK_TOKEN;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  const rawText = await response.text();
  let parsed: any = null;
  try {
    parsed = rawText ? JSON.parse(rawText) : null;
  }
  catch {
    // 仅在请求失败时需要原始文本提示
    if (!response.ok) {
      throw new Error(resolveHttpErrorMessage({
        status: response.status,
        statusText: response.statusText,
        bodyText: rawText,
        fallback: '文件上传失败',
      }));
    }
  }

  if (import.meta.env.DEV) {
    console.log('[chat/upload] response snapshot:', {
      url,
      status: response.status,
      ok: response.ok,
      rawText,
      parsed,
    });
  }

  if (!response.ok) {
    const message = resolveHttpErrorMessage({
      status: response.status,
      statusText: response.statusText,
      jsonMessage: parsed?.message || parsed?.msg,
      bodyText: rawText,
      fallback: '文件上传失败',
    });
    throw new Error(message);
  }

  // 兼容后端使用 code 包裹业务状态的情况（即使 HTTP 200 也可能失败）
  if (parsed && typeof parsed === 'object' && 'code' in parsed) {
    const bizCode = Number(parsed.code);
    if (Number.isFinite(bizCode) && bizCode !== 200 && bizCode !== 0) {
      const message = parsed?.message || parsed?.msg || '文件上传失败';
      throw new Error(message);
    }
  }

  const payload = parsed?.data ?? parsed;
  if (payload && typeof payload === 'object') {
    const normalized = normalizeUploadFileResponse(payload) as UploadFileResponse;
    if (import.meta.env.DEV) {
      console.log('[uploadChatFile] normalized upload response:', {
        raw: payload,
        normalized,
      });
    }
    return normalized;
  }

  throw new Error('文件上传失败');
}

// 语音转文字
export async function audioToText(agentId: string | number, audioFile: File): Promise<AudioToTextResponse> {
  if (!agentId)
    throw new Error('缺少智能体ID');
  if (!audioFile)
    throw new Error('请提供音频文件');

  const formData = new FormData();
  formData.append('file', audioFile, audioFile.name);

  const endpoint = resolveChatEndpoint('/chat/audio');
  const query = new URLSearchParams();
  query.append('agentId', String(agentId));
  const url = `${endpoint}?${query.toString()}`;

  const { useUserStore } = await import('@/stores');
  const userStore = useUserStore();
  const token = userStore.token || DEFAULT_FALLBACK_TOKEN;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  const rawText = await response.text();
  let parsed: any = null;
  try {
    parsed = rawText ? JSON.parse(rawText) : null;
  }
  catch {
    if (!response.ok)
      throw new Error(rawText || '语音转文字失败');
  }

  if (!response.ok) {
    const message = parsed?.message || parsed?.msg || '语音转文字失败';
    throw new Error(message);
  }

  // 处理返回格式：{ "text": "string" }
  // 可能的情况：
  // 1. 直接返回：{ "text": "string" }
  // 2. 标准格式：{ "code": 200, "data": { "text": "string" } }
  // 3. 标准格式：{ "code": 200, "text": "string" }

  if (parsed && typeof parsed === 'object') {
    // 如果是标准格式（有 code 字段）
    if (parsed.code !== undefined) {
      // 检查 code 是否为成功状态
      if (parsed.code === 200 || parsed.code === 0) {
        // 优先从 data 中获取
        if (parsed.data && typeof parsed.data === 'object' && parsed.data.text) {
          return parsed.data as AudioToTextResponse;
        }
        // 或者直接从根对象获取
        if (parsed.text) {
          return { text: parsed.text } as AudioToTextResponse;
        }
      }
      else {
        // code 不是成功状态，抛出错误
        const message = parsed.message || parsed.msg || '语音转文字失败';
        throw new Error(message);
      }
    }
    // 直接返回格式：{ "text": "string" }
    else if (parsed.text) {
      return parsed as AudioToTextResponse;
    }
  }

  throw new Error('语音转文字失败：响应格式不正确');
}
